import {getTestProps} from '@benchling/js-test-utils';
import {Button, Checkbox, createStyles, Divider, Stack, TextInput} from '@mantine/core';
import React, {useEffect, useState} from 'react';

import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

const SIDEBAR_WIDTH = 230;
const ICON_BAR_WIDTH = 40;

const useStyles = createStyles((theme) => ({
  wrapper: {
    display: 'flex',
    flexDirection: 'row',
    height: '100%',
  },
  iconBar: {
    boxSizing: 'border-box',
    width: ICON_BAR_WIDTH,
    minWidth: ICON_BAR_WIDTH,
    height: '100%',
    background: theme.white,
    borderRight: `1px solid ${theme.colors.gray[2]}`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: 0,
    paddingTop: theme.spacing.sm,
  },
  iconButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: theme.spacing.sm,
    borderRadius: theme.radius.md,
    color: theme.colors.gray[6],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 16,
    '&:hover': {
      backgroundColor: theme.colors.gray[1],
    },
  },
  iconButtonActive: {
    color: theme.colors.blue[6],
    backgroundColor: theme.colors.blue[0],
    '&:hover': {
      backgroundColor: theme.colors.blue[1],
    },
  },
  iconButtonWithIndicator: {
    position: 'relative',
  },
  filterIndicator: {
    position: 'absolute',
    top: 4,
    right: 4,
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: theme.colors.blue[6],
  },
  sidebar: {
    boxSizing: 'border-box',
    width: SIDEBAR_WIDTH,
    minWidth: SIDEBAR_WIDTH,
    height: '100%',
    borderRight: `1px solid ${theme.colors.gray[2]}`,
    background: theme.white,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: 0,
    gap: theme.spacing.lg,
    overflow: 'hidden',
  },
  header: {
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
    borderBottom: `1px solid ${theme.colors.gray[2]}`,
    alignSelf: 'stretch',
  },
  headerInner: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.sm,
    flex: 1,
  },
  headerTitle: {
    fontWeight: 600,
    fontSize: theme.fontSizes.md,
    lineHeight: '16px',
    color: theme.colors.gray[9],
    flex: 1,
  },
  collapseButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
    width: 16,
    height: 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.colors.gray[6],
    fontSize: 16,
    lineHeight: 1,
    '&:hover': {
      opacity: 0.7,
    },
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    padding: `0 ${theme.spacing.md}`,
    gap: theme.spacing.md,
    alignSelf: 'stretch',
    flex: 1,
    overflow: 'hidden',
  },
  sectionHeader: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    alignSelf: 'stretch',
  },
  sectionLabel: {
    fontSize: theme.fontSizes.xs,
    fontWeight: 600,
    lineHeight: '14px',
    textTransform: 'uppercase',
    color: theme.colors.gray[9],
  },
  nodeCount: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.gray[5],
    lineHeight: '16px',
  },
  schemaRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
  },
  schemaCount: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.gray[5],
    whiteSpace: 'nowrap',
  },
  selectAllButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: theme.fontSizes.md,
    fontWeight: 400,
    lineHeight: '16px',
    color: theme.colors.blue[6],
    padding: 0,
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  searchInput: {
    alignSelf: 'stretch',
    '& .mantine-TextInput-input': {
      height: 24,
      minHeight: 24,
      borderRadius: theme.radius.md,
      border: `1px solid ${theme.colors.gray[2]}`,
      fontSize: theme.fontSizes.md,
      paddingTop: 0,
      paddingBottom: 0,
    },
    '& .mantine-TextInput-icon': {
      color: theme.colors.gray[3],
    },
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.md,
    flex: '0 1 auto',
    minHeight: 32,
    overflowY: 'auto',
    alignSelf: 'stretch',
    paddingRight: theme.spacing.xl,
  },
  actionDivider: {
    alignSelf: 'stretch',
  },
  actionRow: {
    alignSelf: 'stretch',
    flexShrink: 0,
  },
}));

function areSetsDifferent(a: Set<string>, b: Set<string>): boolean {
  return a.size !== b.size || [...a].some((x) => !b.has(x));
}

interface EntityMapSchemaFilterProps {
  hiddenSchemaIds: Set<string>;
  onChangeHiddenSchemaIds: (hiddenSchemaIds: Set<string>) => void;
  schemas: Array<{id: string; name: string}>;
  visibleNodeCount: number;
  // Kept for future use (e.g. "X of Y" display behind a feature flag)
  totalNodeCount: number;
  nodeCountBySchema: Map<string, {visible: number; total: number}>;
  rootSchemaId: string | null;
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
}

export default function EntityMapSchemaFilter({
  hiddenSchemaIds,
  onChangeHiddenSchemaIds,
  schemas,
  visibleNodeCount,
  nodeCountBySchema,
  rootSchemaId,
  expanded,
  onExpandedChange,
}: EntityMapSchemaFilterProps): JSX.Element | null {
  const {classes, cx} = useStyles();
  const [searchQuery, setSearchQuery] = useState('');
  const [pendingHiddenSchemaIds, setPendingHiddenSchemaIds] = useState<Set<string>>(
    () => new Set(hiddenSchemaIds)
  );

  useEffect(() => {
    setPendingHiddenSchemaIds(new Set(hiddenSchemaIds));
  }, [hiddenSchemaIds]);

  if (schemas.length === 0) {
    return null;
  }

  // Schemas shown in the filter list: user-excluded ones stay so they can be re-enabled,
  // others are hidden when other filters removed all their entities from the graph.
  const schemasShownInFilter = schemas.filter((s) => {
    if (hiddenSchemaIds.size === 0 || hiddenSchemaIds.has(s.id) || s.id === rootSchemaId) {
      return true;
    }
    const counts = nodeCountBySchema.get(s.name);
    return counts != null && counts.visible > 0;
  });

  const filteredSchemas = searchQuery
    ? schemasShownInFilter.filter((s) => s.name.toLowerCase().includes(searchQuery.toLowerCase()))
    : schemasShownInFilter;

  const handleToggleSchema = (schemaId: string): void => {
    setPendingHiddenSchemaIds((prev) => {
      const next = new Set(prev);
      if (next.has(schemaId)) {
        next.delete(schemaId);
      } else {
        next.add(schemaId);
      }
      return next;
    });
  };

  const handleSelectAll = (): void => {
    setPendingHiddenSchemaIds(new Set());
  };

  const handleDeselectAll = (): void => {
    setPendingHiddenSchemaIds(new Set(schemas.map((s) => s.id)));
  };

  const onApplyFilters = (): void => {
    onChangeHiddenSchemaIds(pendingHiddenSchemaIds);
  };

  const onResetFilters = (): void => {
    setPendingHiddenSchemaIds(new Set(hiddenSchemaIds));
  };

  const allSelected = pendingHiddenSchemaIds.size === 0;
  const hasPendingChanges = areSetsDifferent(pendingHiddenSchemaIds, hiddenSchemaIds);

  return (
    <div className={classes.wrapper} {...getTestProps({component: 'EntityMapSchemaFilter', element: 'root'})}>
      <div className={classes.iconBar}>
        <button
          type="button"
          className={cx(
            classes.iconButton,
            expanded && classes.iconButtonActive,
            !expanded && hiddenSchemaIds.size > 0 && classes.iconButtonWithIndicator
          )}
          onClick={() => {
            onExpandedChange(!expanded);
          }}
          aria-label="Toggle filters"
          {...getTestProps({component: 'EntityMapSchemaFilter', element: 'toggle-button'})}
        >
          <BenchlingIcon icon="filter-menu" />
          {!expanded && hiddenSchemaIds.size > 0 && (
            <span
              aria-hidden="true"
              className={classes.filterIndicator}
              {...getTestProps({component: 'EntityMapSchemaFilter', element: 'filter-indicator'})}
            />
          )}
        </button>
      </div>
      {expanded && (
        <div className={classes.sidebar}>
          <div className={classes.header}>
            <div className={classes.headerInner}>
              <span className={classes.headerTitle}>Filters</span>
              <button
                type="button"
                className={classes.collapseButton}
                onClick={() => {
                  onExpandedChange(false);
                }}
                aria-label="Collapse filters"
                {...getTestProps({component: 'EntityMapSchemaFilter', element: 'collapse-button'})}
              >
                <BenchlingIcon icon="chevron-left" />
              </button>
            </div>
          </div>
          <div className={classes.content}>
            <div className={classes.sectionHeader}>
              <span className={classes.sectionLabel}>Entity Schemas</span>
              <button
                type="button"
                className={classes.selectAllButton}
                onClick={allSelected ? handleDeselectAll : handleSelectAll}
                {...getTestProps({component: 'EntityMapSchemaFilter', element: 'select-all'})}
              >
                {allSelected ? 'Deselect all' : 'Select all'}
              </button>
            </div>
            <span className={classes.nodeCount}>
              Showing {visibleNodeCount} {visibleNodeCount === 1 ? 'entity' : 'entities'}
            </span>
            <div className={classes.searchInput}>
              <TextInput
                placeholder="Search"
                aria-label="Search schemas"
                size="xs"
                icon={<BenchlingIcon icon="search" />}
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.currentTarget.value);
                }}
                {...getTestProps({component: 'EntityMapSchemaFilter', element: 'search'})}
              />
            </div>
            <div className={classes.list}>
              {filteredSchemas.map((schema) => {
                const counts = nodeCountBySchema.get(schema.name);
                const isRootSchema = schema.id === rootSchemaId;
                const isPendingHidden = pendingHiddenSchemaIds.has(schema.id);
                const isAppliedHidden = hiddenSchemaIds.has(schema.id);
                return (
                  <div key={schema.id} className={classes.schemaRow}>
                    <Checkbox
                      label={schema.name}
                      aria-label={schema.name}
                      checked={!isPendingHidden}
                      radius="xs"
                      onChange={() => {
                        handleToggleSchema(schema.id);
                      }}
                      {...getTestProps({component: 'EntityMapSchemaFilter', element: 'schema'})}
                    />
                    <span className={classes.schemaCount}>
                      {isRootSchema && isAppliedHidden ? 'Main' : (counts?.visible ?? 0)}
                    </span>
                  </div>
                );
              })}
            </div>
            <Divider my={0} className={classes.actionDivider} />
            <Stack spacing="xs" pb="md" className={classes.actionRow}>
              <Button
                variant={hasPendingChanges ? 'primary' : 'secondary'}
                size="xs"
                radius="sm"
                onClick={onApplyFilters}
                disabled={!hasPendingChanges}
                fullWidth={true}
                {...getTestProps({component: 'EntityMapSchemaFilter', element: 'apply-filter'})}
              >
                Apply Changes
              </Button>
              {hasPendingChanges && (
                <Button
                  variant="secondary"
                  size="xs"
                  radius="sm"
                  onClick={onResetFilters}
                  fullWidth={true}
                  {...getTestProps({component: 'EntityMapSchemaFilter', element: 'reset-filters'})}
                >
                  Remove Changes
                </Button>
              )}
            </Stack>
          </div>
        </div>
      )}
    </div>
  );
}
